import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Bell, ShoppingBag, Clock, CheckCircle2, Truck, Euro, Plus, Edit2, Trash2, Loader2, Volume2, VolumeX, Link2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DashboardShell, { StatCard, EmptyState } from "@/components/DashboardShell";
import OrderCard from "@/components/OrderCard";
import ProfileEditor from "@/components/ProfileEditor";
import { downloadPdf } from "@/lib/download";
import { eur, STATUS_LABEL } from "@/lib/status";
import api, { apiError } from "@/lib/api";

const NEXT = {
  in_attesa: [{ s: "accettato", l: "Accetta" }, { s: "rifiutato", l: "Rifiuta", danger: true }],
  accettato: [{ s: "in_preparazione", l: "In preparazione" }],
  in_preparazione: [{ s: "pronto", l: "Pronto" }, { s: "in_consegna", l: "In consegna" }],
  pronto: [{ s: "consegnato", l: "Consegnato" }],
  in_consegna: [{ s: "consegnato", l: "Consegnato" }],
};

export default function VendorDashboard() {
  const [tab, setTab] = useState("overview");
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [pkgDialog, setPkgDialog] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const prevNew = useRef(0);

  const load = () => {
    api.get("/vendor/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/vendor/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/vendor/orders").then(({ data }) => {
      setOrders(data);
      const newCount = data.filter((o) => o.status === "in_attesa").length;
      if (newCount > prevNew.current && prevNew.current !== 0 && soundOn) beep();
      prevNew.current = newCount;
    }).catch(() => {});
    api.get("/vendor/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/vendor/partnership-requests").then(({ data }) => setRequests(data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [soundOn]);

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination); o.frequency.value = 880; o.type = "sine";
      g.gain.setValueAtTime(0.2, ctx.currentTime); o.start();
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5); o.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  };

  const setStatus = async (orderId, status, reason = "") => {
    try { await api.patch(`/orders/${orderId}/status`, { status, reason }); toast.success("Stato aggiornato"); setRejectFor(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleAvailable = async (v) => {
    try { await api.put("/vendor/me", { available: v }); setMe({ ...me, available: v }); toast.success(v ? "Locale disponibile" : "Locale temporaneamente non disponibile"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const handleRequest = async (id, action) => {
    try { await api.post(`/vendor/partnership-requests/${id}/${action}`); toast.success("Fatto"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const delPackage = async (id) => { await api.delete(`/vendor/packages/${id}`); toast.success("Pacchetto eliminato"); load(); };

  const newOrders = orders.filter((o) => o.status === "in_attesa");
  const tabs = [
    { key: "overview", label: "Panoramica" },
    { key: "orders", label: "Ordini", badge: newOrders.length },
    { key: "packages", label: "Menu & pacchetti" },
    { key: "requests", label: "Convenzioni", badge: requests.length },
    { key: "profile", label: "Profilo" },
  ];

  return (
    <DashboardShell title={me?.name || "Dashboard Locale"} subtitle={me?.type ? `${me.type} · ${me.city}` : ""} tabs={tabs} active={tab} onTab={setTab}
      headerRight={
        <div className="flex items-center gap-3">
          <button onClick={() => setSoundOn(!soundOn)} className="w-10 h-10 rounded-full bg-white border border-navy/10 flex items-center justify-center" data-testid="sound-toggle">
            {soundOn ? <Volume2 className="w-5 h-5 text-navy" /> : <VolumeX className="w-5 h-5 text-navy/40" />}
          </button>
          <div className="flex items-center gap-2 bg-white border border-navy/10 rounded-full px-3 py-1.5">
            <span className="text-xs text-navy/60">Disponibile</span>
            <Switch checked={me?.available ?? true} onCheckedChange={toggleAvailable} data-testid="availability-toggle" />
          </div>
        </div>
      }>
      {tab === "overview" && stats && (
        <div className="space-y-6">
          {!me?.available && <div className="bg-errorbg text-errortext rounded-2xl p-4 text-sm">Il locale è impostato come <b>temporaneamente non disponibile</b>. Gli ospiti non possono ordinare.</div>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Nuovi ordini" value={stats.new_orders} icon={Bell} />
            <StatCard label="In preparazione" value={stats.in_preparation} icon={Clock} />
            <StatCard label="Pronti" value={stats.ready} icon={CheckCircle2} />
            <StatCard label="Consegne" value={stats.delivering} icon={Truck} />
            <StatCard label="Vendite oggi" value={eur(stats.sales_today)} icon={Euro} />
            <StatCard label="Vendite del mese" value={eur(stats.sales_month)} icon={Euro} />
            <StatCard label="Da incassare" value={eur(stats.due)} icon={ShoppingBag} />
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => downloadPdf(`/vendor/settlement.pdf?period=month`, "riepilogo-mese.pdf")} variant="outline" className="rounded-full" data-testid="vendor-settlement-month"><Download className="w-4 h-4 mr-1" />Riepilogo mese (PDF)</Button>
            <Button onClick={() => downloadPdf(`/vendor/settlement.pdf?period=week`, "riepilogo-settimana.pdf")} variant="outline" className="rounded-full" data-testid="vendor-settlement-week"><Download className="w-4 h-4 mr-1" />Riepilogo settimana (PDF)</Button>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="grid md:grid-cols-2 gap-4">
          {orders.length === 0 && <EmptyState text="Nessun ordine ricevuto" />}
          {orders.map((o) => (
            <OrderCard key={o.id} order={o} showWhatsapp>
              <div className="flex flex-wrap gap-2">
                {(NEXT[o.status] || []).map((n) => (
                  <Button key={n.s} size="sm" onClick={() => n.s === "rifiutato" ? setRejectFor(o) : setStatus(o.id, n.s)}
                    className={`rounded-full ${n.danger ? "bg-errortext text-white" : "bg-navy text-white"}`} data-testid={`status-${o.code}-${n.s}`}>
                    {n.l}
                  </Button>
                ))}
                {!NEXT[o.status] && <span className="text-xs text-navy/40">Ordine {STATUS_LABEL(o.status).label.toLowerCase()}</span>}
              </div>
            </OrderCard>
          ))}
        </div>
      )}

      {tab === "packages" && (
        <div>
          <Button onClick={() => setPkgDialog({})} className="rounded-full bg-orange hover:bg-orange-600 text-white mb-4" data-testid="add-package-btn"><Plus className="w-4 h-4 mr-1" />Nuovo pacchetto</Button>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.length === 0 && <EmptyState text="Nessun pacchetto. Crea il primo!" />}
            {packages.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-navy/10 p-5" data-testid={`vpkg-${p.id}`}>
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-navy">{p.name}</p>
                  <div className="flex gap-1">
                    <button onClick={() => setPkgDialog(p)} className="text-navy/50 hover:text-navy"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => delPackage(p.id)} className="text-errortext"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="text-xs text-navy/50 mt-1">{p.description}</p>
                <p className="mt-2 font-bold text-orange">{eur(p.vendor_price_pp)} <span className="text-xs text-navy/40 font-normal">/pers.</span></p>
                {!p.available && <Badge variant="outline" className="mt-2 text-xs text-errortext">Non disponibile</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "requests" && (
        <div className="space-y-3">
          {requests.length === 0 && <EmptyState icon={Link2} text="Nessuna richiesta di convenzione" />}
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-navy/10 p-4 flex items-center justify-between gap-3" data-testid={`req-${r.id}`}>
              <div><p className="font-medium text-navy">{r.bnb_name}</p><p className="text-xs text-navy/50">richiede una convenzione</p></div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleRequest(r.id, "accept")} className="rounded-full bg-successtext text-white" data-testid={`req-accept-${r.id}`}>Accetta</Button>
                <Button size="sm" variant="outline" onClick={() => handleRequest(r.id, "reject")} className="rounded-full text-errortext border-errortext/30" data-testid={`req-reject-${r.id}`}>Rifiuta</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "profile" && <ProfileEditor role="vendor" me={me} onSaved={load} />}

      <PackageDialog data={pkgDialog} onClose={() => setPkgDialog(null)} onSaved={() => { setPkgDialog(null); load(); }} />      <RejectDialog order={rejectFor} onClose={() => setRejectFor(null)} onConfirm={(reason) => setStatus(rejectFor.id, "rifiutato", reason)} />
    </DashboardShell>
  );
}

function PackageDialog({ data, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", type: "custom", vendor_price_pp: 0, public_price_pp: 0, description: "", products: "", allergens: "", available: true });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (data && data.id) setF({ ...data, products: (data.products || []).join(", "), allergens: (data.allergens || []).join(", ") });
    else if (data) setF({ name: "", type: "custom", vendor_price_pp: 0, public_price_pp: 0, description: "", products: "", allergens: "", available: true });
  }, [data]);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    if (!f.name || !f.vendor_price_pp) { toast.error("Nome e prezzo obbligatori"); return; }
    setLoading(true);
    const payload = {
      name: f.name, type: f.type || "custom", vendor_price_pp: Number(f.vendor_price_pp),
      public_price_pp: Number(f.public_price_pp || 0), description: f.description,
      products: f.products.split(",").map((s) => s.trim()).filter(Boolean),
      allergens: f.allergens.split(",").map((s) => s.trim()).filter(Boolean), available: f.available,
    };
    try {
      if (data.id) await api.put(`/vendor/packages/${data.id}`, payload);
      else await api.post("/vendor/packages", payload);
      toast.success("Pacchetto salvato"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-auto" data-testid="package-dialog">
        <DialogHeader><DialogTitle className="text-navy font-heading">{data?.id ? "Modifica pacchetto" : "Nuovo pacchetto"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl mt-1" data-testid="pkg-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prezzo locale € /pers.</Label><Input type="number" step="0.01" value={f.vendor_price_pp} onChange={(e) => set("vendor_price_pp", e.target.value)} className="rounded-xl mt-1" data-testid="pkg-price" /></div>
            <div><Label>Prezzo pubblico € /pers.</Label><Input type="number" step="0.01" value={f.public_price_pp} onChange={(e) => set("public_price_pp", e.target.value)} className="rounded-xl mt-1" data-testid="pkg-public" /></div>
          </div>
          <div><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl mt-1" rows={2} data-testid="pkg-desc" /></div>
          <div><Label>Prodotti (separati da virgola)</Label><Input value={f.products} onChange={(e) => set("products", e.target.value)} className="rounded-xl mt-1" placeholder="Caffè, Cornetto" data-testid="pkg-products" /></div>
          <div><Label>Allergeni (separati da virgola)</Label><Input value={f.allergens} onChange={(e) => set("allergens", e.target.value)} className="rounded-xl mt-1" placeholder="Glutine, Latte" data-testid="pkg-allergens" /></div>
          <label className="flex items-center justify-between"><span className="text-sm text-navy/70">Disponibile</span><Switch checked={f.available} onCheckedChange={(v) => set("available", v)} data-testid="pkg-available" /></label>
          <Button onClick={save} disabled={loading} className="w-full rounded-full bg-navy text-white h-11" data-testid="pkg-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva pacchetto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ order, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  useEffect(() => { setReason(""); }, [order]);
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="reject-dialog">
        <DialogHeader><DialogTitle className="text-navy font-heading">Rifiuta ordine {order?.code}</DialogTitle></DialogHeader>
        <Label>Motivo del rifiuto</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl mt-1" rows={3} placeholder="es. Prodotto non disponibile" data-testid="reject-reason" />
        <Button onClick={() => onConfirm(reason)} className="w-full rounded-full bg-errortext text-white h-11 mt-2" data-testid="reject-confirm">Conferma rifiuto</Button>
      </DialogContent>
    </Dialog>
  );
}
